<template>
  <component
    :is="as"
    ref="textRef"
    v-bind="rootAttrs"
    :class="rootClass"
    :style="rootStyle"
    :tabindex="isFocusable ? 0 : undefined"
    :aria-describedby="tooltipOpen ? tooltipId : undefined"
    @mouseenter="openTooltip"
    @mouseleave="closeTooltip"
    @focusin="openTooltip"
    @focusout="closeTooltip"
  >
    <slot>{{ text }}</slot>
  </component>

  <Teleport to="body">
    <Transition name="ui-truncated-text-tooltip">
      <span
        v-if="tooltipOpen && tooltipContent"
        :id="tooltipId"
        ref="tooltipRef"
        class="ui-truncated-text__tooltip"
        :class="`ui-truncated-text__tooltip--${resolvedPlacement}`"
        :style="tooltipStyle"
        role="tooltip"
      >
        {{ tooltipContent }}
      </span>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useAttrs,
  watch
} from 'vue';
import type { Component, StyleValue } from 'vue';

defineOptions({ inheritAttrs: false });

type TooltipPlacement = 'top' | 'bottom';

const props = withDefaults(
  defineProps<{
    as?: string | Component;
    text?: unknown;
    lines?: number;
    tooltipPlacement?: TooltipPlacement;
    focusable?: boolean;
    disabled?: boolean;
  }>(),
  {
    as: 'span',
    text: () => '',
    lines: undefined,
    tooltipPlacement: 'top',
    focusable: true,
    disabled: false
  }
);

const attrs = useAttrs();
const textRef = ref<HTMLElement | null>(null);
const tooltipRef = ref<HTMLElement | null>(null);
const isTruncated = ref(false);
const tooltipOpen = ref(false);
const tooltipContent = ref('');
const resolvedPlacement = ref<TooltipPlacement>(props.tooltipPlacement);
const tooltipStyle = ref<Record<string, string>>({});

const tooltipId = `ui-truncated-text-${Math.random().toString(36).slice(2, 10)}`;

let resizeObserver: ResizeObserver | null = null;

const rootAttrs = computed(() => {
  const rest: Record<string, unknown> = { ...attrs };
  delete rest.class;
  delete rest.style;
  return rest;
});

const rootClass = computed(() => [
  attrs.class,
  'ui-truncated-text',
  props.lines !== undefined && 'ui-truncated-text--clamped',
  props.lines === 1 && 'ui-truncated-text--single-line'
]);

const rootStyle = computed<StyleValue>(() => {
  if (props.lines === undefined) return attrs.style as StyleValue;

  return [
    attrs.style,
    {
      '--ui-truncated-text-lines': String(props.lines)
    }
  ] as StyleValue;
});

const isFocusable = computed(() => props.focusable && isTruncated.value && !props.disabled);

const textValue = () => {
  if (props.text !== undefined && props.text !== null && String(props.text).trim()) {
    return String(props.text).trim();
  }

  return (textRef.value?.innerText ?? textRef.value?.textContent ?? '').trim();
};

const updateTruncation = () => {
  const element = textRef.value;
  if (!element || props.disabled) {
    isTruncated.value = false;
    tooltipOpen.value = false;
    return;
  }

  const horizontalOverflow = element.scrollWidth > element.clientWidth + 1;
  const verticalOverflow = element.scrollHeight > element.clientHeight + 1;
  isTruncated.value = horizontalOverflow || verticalOverflow;

  if (!isTruncated.value) {
    tooltipOpen.value = false;
  }
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const positionTooltip = () => {
  const target = textRef.value;
  const tooltip = tooltipRef.value;
  if (!target || !tooltip || !tooltipOpen.value) return;

  const targetRect = target.getBoundingClientRect();
  const tooltipRect = tooltip.getBoundingClientRect();
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const viewportMargin = 12;
  const gap = 8;
  const maxWidth = Math.max(160, Math.min(360, viewportWidth - viewportMargin * 2));
  const centeredLeft = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
  const left = clamp(centeredLeft, viewportMargin, viewportWidth - tooltipRect.width - viewportMargin);
  const topPosition = targetRect.top - tooltipRect.height - gap;
  const bottomPosition = targetRect.bottom + gap;
  const preferredPlacement = props.tooltipPlacement;
  const canOpenTop = topPosition >= viewportMargin;
  const canOpenBottom = bottomPosition + tooltipRect.height <= viewportHeight - viewportMargin;
  const placement: TooltipPlacement = preferredPlacement === 'top'
    ? (canOpenTop || !canOpenBottom ? 'top' : 'bottom')
    : (canOpenBottom || !canOpenTop ? 'bottom' : 'top');
  const top = placement === 'top' ? Math.max(viewportMargin, topPosition) : bottomPosition;

  resolvedPlacement.value = placement;
  tooltipStyle.value = {
    left: `${left}px`,
    maxWidth: `${maxWidth}px`,
    top: `${Math.min(top, viewportHeight - tooltipRect.height - viewportMargin)}px`
  };
};

const openTooltip = () => {
  updateTruncation();
  if (!isTruncated.value || props.disabled) return;

  tooltipContent.value = textValue();
  if (!tooltipContent.value) return;

  tooltipOpen.value = true;
  tooltipStyle.value = {};
  void nextTick(positionTooltip);
};

const closeTooltip = () => {
  tooltipOpen.value = false;
};

const handleViewportChange = () => {
  updateTruncation();
  if (tooltipOpen.value) {
    void nextTick(positionTooltip);
  }
};

onMounted(() => {
  void nextTick(updateTruncation);

  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(handleViewportChange);
    if (textRef.value) {
      resizeObserver.observe(textRef.value);
    }
  }

  window.addEventListener('resize', handleViewportChange);
  window.addEventListener('scroll', handleViewportChange, true);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  window.removeEventListener('resize', handleViewportChange);
  window.removeEventListener('scroll', handleViewportChange, true);
});

watch(
  () => [props.text, props.lines, props.disabled],
  () => {
    void nextTick(updateTruncation);
  }
);
</script>

<style scoped>
.ui-truncated-text {
  min-width: 0;
  max-width: 100%;
}

.ui-truncated-text--clamped {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: var(--ui-truncated-text-lines);
  overflow-wrap: anywhere;
  text-overflow: ellipsis;
}

.ui-truncated-text--single-line {
  display: block;
  white-space: nowrap;
}

.ui-truncated-text__tooltip {
  position: fixed;
  z-index: 10000;
  box-sizing: border-box;
  overflow-wrap: anywhere;
  border: 1px solid color-mix(in srgb, var(--ui-border) 82%, var(--ui-foreground) 18%);
  border-radius: var(--ui-radius-md);
  background: color-mix(in srgb, var(--ui-surface) 96%, white 4%);
  box-shadow: var(--ui-shadow-popover);
  color: var(--ui-foreground);
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1.35;
  padding: 0.55rem 0.7rem;
  pointer-events: none;
}

.ui-truncated-text-tooltip-enter-active,
.ui-truncated-text-tooltip-leave-active {
  transition:
    opacity 0.14s ease,
    transform 0.14s ease;
}

.ui-truncated-text-tooltip-enter-from,
.ui-truncated-text-tooltip-leave-to {
  opacity: 0;
  transform: translateY(2px);
}

.ui-truncated-text__tooltip--bottom.ui-truncated-text-tooltip-enter-from,
.ui-truncated-text__tooltip--bottom.ui-truncated-text-tooltip-leave-to {
  transform: translateY(-2px);
}

:global([data-theme='dark']) .ui-truncated-text__tooltip {
  border-color: color-mix(in srgb, var(--ui-border) 76%, white 14%);
  background: color-mix(in srgb, var(--ui-surface) 92%, white 8%);
  color: var(--ui-foreground);
}
</style>
